/**
 * A command executor for the import canned content process. Commands can run in background or in foreground, depending
 * on whether an initiating command was called.
 *
 * Created by eliranm on 14/12/14.
 */
define(function (require) {
    'use strict';

    var LE = require('app');
    var CONST = require('const');
    var NumberFormats = require('infra-util/NumberFormats');
    var ContentImportCommandModel = require('content-models/ContentImportCommandModel');
    var ClickableItemView = require('src/views/ClickableItemView');

    var ContentImportCommandController = {};

    LE.module('LEContent', function (LEContent, LE, Backbone, Marionette, $, _) {

        ContentImportCommandController = Marionette.Controller.extend({


            // overridden

            initialize: function () {
                this.model = new ContentImportCommandModel();
                this.listenTo(this.model, 'change:' + ContentImportCommandModel.KEYS.mode, this._onModelModeChange);
            },


            // public

            /**
             * Invokes the appropriate command according to the background/foreground state of the controller.
             *
             * @param commandType {string} The command type. This acts as the notification type to show in case  the
             * command runs in background. Use values from ContentImportCommandController.COMMAND_TYPE.
             * Possible values are:
             * ContentImportCommandController.COMMAND_TYPE.SUCCESS,
             * ContentImportCommandController.COMMAND_TYPE.ERROR,
             * ContentImportCommandController.COMMAND_TYPE.PROGRESS.
             * @param backgroundCommandCallback {function} A command to execute in case the controller is in background
             * mode. This command will run when the top bar notification is clicked.
             * @param foregroundCommand {function} A command to execute in case the controller is in foreground mode.
             * This command will run immediately. Foreground commands only runs on terminating commands, i.e. when the
             * notification is of a type that will terminate the background process.
             * See ContentImportCommandController.CONFIG.terminatingNotifications.
             * @param options {object} Optional settings for the command:
             *      - notificationMessageArgs {array} Arbitrary data to be used in the background notification message,
             *      if the message requires dynamic values.
             *      - commandsContext {object} The context in which the background and foreground command functions will run.
             *
             * @see ContentImportCommandController.CONFIG.terminatingNotifications.
             */
            invoke: function (commandType, backgroundCommandCallback, foregroundCommand, options) {
                options = options || {};

                var initiating = this._isCommandInitiating(commandType);
                if (initiating) {
                    this._invokeInitiatingCommand(backgroundCommandCallback, commandType, options.notificationMessageArgs, options.commandsContext);
                }

                // todo - _invokeIndifferentCommand() ? (for INFO type notifications)

                var terminating = this._isCommandTerminating(commandType);
                if (terminating) {
                    this._invokeTerminatingCommand(backgroundCommandCallback, foregroundCommand, commandType, options.notificationMessageArgs, options.commandsContext);
                }
            },

            /**
             * Sets the controller's mode. Use values from ContentImportCommandController.CONFIG.mode.
             * @param mode A new mode for the controller.
             */
            setMode: function (mode) {
                if (_.contains(ContentImportCommandController.CONFIG.mode, mode)) {
                    this.model.setMode(mode);
                }
            },

            /**
             * Checks if the controller is running tasks in foreground.
             * @returns {boolean} <code>true</code> if the controller is running tasks in foreground, <code>false</code> otherwise.
             */
            isForegroundMode: function () {
                return this.model.getMode() === ContentImportCommandController.CONFIG.mode.FOREGROUND;
            },

            /**
             * Checks if the controller is running tasks in background.
             * @returns {boolean} <code>true</code> if the controller is running tasks in background, <code>false</code> otherwise.
             */
            isBackgroundMode: function () {
                return this.model.getMode() === ContentImportCommandController.CONFIG.mode.BACKGROUND;
            },

            /**
             * Resets the controller by setting the mode to its default state (foreground) and clearing all top-bar
             * notifications.
             */
            reset: function () {
                this.model.reset();
                this._clearTopBarNotification();
            },


            // private event handlers

            _onModelModeChange: function (model, newVal) {
                LEContent.context.set(LEContent.context.KEYS.cannedContentImportRunningInBackground,
                        newVal === ContentImportCommandController.CONFIG.mode.BACKGROUND);
            },


            // private

            _invokeInitiatingCommand: function (backgroundCommandCallback, commandType, notificationMessageArgs, commandsContext) {
                if (this.isForegroundMode()) {
                    this.model.setMode(ContentImportCommandController.CONFIG.mode.BACKGROUND);
                    this._clearTopBarNotification();
                    this._updateTopBarNotification(commandType, backgroundCommandCallback, null, false, commandsContext);
                }
            },

            _invokeTerminatingCommand: function (backgroundCommandCallback, foregroundCommand, commandType, notificationMessageArgs, commandsContext) {
                if (this.isBackgroundMode()) {
                    this._updateTopBarNotification(commandType, backgroundCommandCallback, notificationMessageArgs, true, commandsContext);
                    this.model.setMode(ContentImportCommandController.CONFIG.mode.FOREGROUND);
                } else {
                    this._clearTopBarNotification();
                    if (_.isFunction(foregroundCommand)) {
                        foregroundCommand.call(this._getContext(commandsContext));
                    }
                }
            },

            _isCommandTerminating: function (commandType) {
                return _.contains(ContentImportCommandController.CONFIG.terminatingNotifications, commandType);
            },

            _isCommandInitiating: function (commandType) {
                return _.contains(ContentImportCommandController.CONFIG.initiatingNotifications, commandType);
            },

            _updateTopBarNotification: function (notificationType, onClick, notificationMessageArgs, removeOnClose, context) {
                var _message,
                    _onClick,
                    _context = this._getContext(context);
                if (!_.contains(CONST.TOP_BAR_NOTIFICATION_TYPE, notificationType)) {
                    LEContent.logger.graph({name: "content:ContentImportCommandController:_updateTopBarNotification:error"});
                    LEContent.logger.error('notification type is not allowed', 'ContentImportCommandController:_updateTopBarNotification', notificationType);
                    return;
                }
                if (onClick && !_.isFunction(onClick)) {
                    LEContent.logger.graph({name: "content:ContentImportCommandController:_updateTopBarNotification:error"});
                    LEContent.logger.error('onClick must be a function', 'ContentImportCommandController:_updateTopBarNotification', onClick);
                    return;
                }
                _message = this._getTopBarNotificationMessage(notificationType, notificationMessageArgs);
                if (!_message) {
                    LEContent.logger.graph({name: "content:ContentImportCommandController:_updateTopBarNotification:error"});
                    LEContent.logger.error('failed to get top bar notification message for notification type ' + notificationType, 'ContentImportCommandController:_updateTopBarNotification');
                    return;
                }
                // wrapping the onClick to clear notification center when clicking on the notification contents
                _onClick = _.bind(function () {
                    this._clearTopBarNotification();
                    onClick.apply(_context, arguments);
                }, this);
                this._addTopBarNotification(notificationType, _message, _onClick, removeOnClose);
            },

            _addTopBarNotification: function (notificationType, message, onClick, removeOnClose) {
                this.topBarNotification = LE.notificationCenter.notify({
                    TopBarNotification: {
                        type: notificationType,
                        removeOnClose: removeOnClose,
                        viewOptions: {
                            getContentViewCallBack: function () {
                                return new ClickableItemView({
                                    text: message,
                                    onClick: onClick
                                });
                            }
                        }
                    }});
            },

            _clearTopBarNotification: function () {
                if (!this.topBarNotification) {
                    return;
                }
                LE.notificationCenter.clearNotification({
                    TopBarNotification: {
                        id: this.topBarNotification.TopBarNotification
                    }
                });
                this.topBarNotification = null;
            },

            _getTopBarNotificationMessage: function (notificationType, notificationMessageArgs) {
                var messageConfig, res, importedItemsSize;
                switch (notificationType) {
                    case CONST.TOP_BAR_NOTIFICATION_TYPE.PROGRESS:
                        messageConfig = 'LEContent.content.import.notification.topBar.progress';
                        break;
                    case CONST.TOP_BAR_NOTIFICATION_TYPE.SUCCESS:
                        importedItemsSize = notificationMessageArgs[0];
                        if (_.isNaN(importedItemsSize)) {
                            LEContent.logger.graph({name: "content:ContentImportCommandController:_updateTopBarNotification:error"});
                            LEContent.logger.error('cannot create notification success message; imported items size is not a number', 'ContentImportCommandController:_getTopBarNotificationMessage');
                            break;
                        }
                        messageConfig = {
                            key: 'LEContent.content.import.notification.topBar.success',
                            args: [
                                importedItemsSize,
                                {
                                    key: NumberFormats.isPlural(importedItemsSize) ? 'LEContent.content.import.notification.topBar.success.plural' : 'LEContent.content.import.notification.topBar.success.singular',
                                    isTranslate: true
                                }
                            ]
                        };
                        break;
                    case CONST.TOP_BAR_NOTIFICATION_TYPE.ERROR:
                        messageConfig = 'LEContent.content.import.notification.topBar.error';
                        break;
                }
                if (messageConfig) {
                    res = LE.translator.translate(messageConfig);
                }
                return res;
            },

            _getContext: function (context) {
                return context || this;
            }

        });
    });

    ContentImportCommandController.COMMAND_TYPE = CONST.TOP_BAR_NOTIFICATION_TYPE;

    ContentImportCommandController.CONFIG = {
        mode: {
            BACKGROUND: 'background',
            FOREGROUND: 'foreground'
        },
        initiatingNotifications: [
            ContentImportCommandController.COMMAND_TYPE.PROGRESS
        ],
        terminatingNotifications: [
            ContentImportCommandController.COMMAND_TYPE.SUCCESS,
            ContentImportCommandController.COMMAND_TYPE.ERROR
        ]
    };

    return ContentImportCommandController;
})
;
